import type { ReactNode } from 'react';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  EvimoButton,
  type EvimoButtonProps,
} from '~/app/components/common/EvimoButton/EvimoButton';

type FilterBarActionsProps = {
  children: ReactNode;
  className?: string;
};

/** Right-aligned toolbar actions (Refresh first, primary action last). */
export function FilterBarActions({
  children,
  className,
}: FilterBarActionsProps) {
  return (
    <div
      className={['evimo-filter-bar__actions', className]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </div>
  );
}

type FilterBarRefreshButtonProps = Omit<
  EvimoButtonProps,
  'icon' | 'iconLeft' | 'children' | 'type'
> & {
  /** @default buttons.refresh */
  labelKey?: string;
  ns?: string | readonly string[];
  defaultLabel?: string;
};

export function FilterBarRefreshButton({
  labelKey = 'buttons.refresh',
  ns = 'common',
  defaultLabel = 'Refresh',
  loading,
  onClick,
  ...rest
}: FilterBarRefreshButtonProps) {
  const { t } = useTranslation(ns);

  return (
    <EvimoButton
      htmlType="button"
      type="default"
      colorVariant="secondary"
      iconLeft={<ReloadOutlined />}
      loading={loading}
      onClick={onClick}
      {...rest}
    >
      {t(labelKey, defaultLabel)}
    </EvimoButton>
  );
}

type FilterBarAddButtonProps = Omit<
  EvimoButtonProps,
  'type' | 'icon' | 'iconLeft' | 'children'
> & {
  ns?: string | readonly string[];
  /** Pre-translated label (use when the parent already resolved i18n). */
  label?: string;
  /** Full button label key (e.g. "overview.addAsset"). */
  labelKey?: string;
  /** Fallback when the key is missing (English). */
  defaultLabel?: string;
  /** i18n interpolation values (e.g. `{ role: 'supervisor' }`). */
  labelValues?: Record<string, string>;
};

export function FilterBarAddButton({
  label,
  labelKey,
  ns = 'common',
  defaultLabel = 'Add',
  labelValues,
  disabled,
  loading,
  onClick,
  ...rest
}: FilterBarAddButtonProps) {
  const { t } = useTranslation(ns);
  const buttonLabel =
    label ??
    (labelKey
      ? t(labelKey, { defaultValue: defaultLabel, ...labelValues })
      : defaultLabel);

  return (
    <EvimoButton
      type="primary"
      colorVariant="inverse"
      iconLeft={<PlusOutlined />}
      disabled={disabled}
      loading={loading}
      onClick={onClick}
      {...rest}
    >
      {buttonLabel}
    </EvimoButton>
  );
}
